import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { LanguageService } from '../../../services/i18n/language';
import { UserService, UserDto, CreateUpdateUserRequest } from '../../../services/api/user';
import { DictionaryService, RoleDto } from '../../../services/api/dictionary';
import { AuthService } from '../../../services/auth/auth';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-user-edit',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule],
  templateUrl: './user-edit.html',
  styleUrl: './user-edit.scss'
})
export class UserEditComponent implements OnInit, OnDestroy {
  userForm: FormGroup;
  user: UserDto | null = null;
  roles: RoleDto[] = [];
  loading = false;
  submitting = false;
  error: string | null = null;
  isEditMode = false;
  userId: number | null = null;

  // Translations
  titleText = 'Создать пользователя';
  editTitleText = 'Редактировать пользователя';
  backText = 'Назад';
  submitText = 'Создать';
  editSubmitText = 'Сохранить';
  submittingText = 'Создание...';
  editSubmittingText = 'Сохранение...';
  nameLabel = 'Имя';
  emailLabel = 'Email';
  phoneLabel = 'Телефон';
  roleLabel = 'Роль';
  passwordLabel = 'Пароль';
  passwordPlaceholder = 'Оставьте пустым, чтобы не менять';
  commentLabel = 'Комментарий';
  cancelText = 'Отмена';
  selectRoleText = 'Выберите роль';
  loadingText = 'Загрузка...';
  nameRequiredError = 'Имя обязательно';
  emailRequiredError = 'Email обязателен';
  emailInvalidError = 'Email должен быть валидным';
  roleRequiredError = 'Роль обязательна';
  fieldRequiredError = 'Поле обязательно';
  errorNotFoundText = 'Пользователь не найден';
  errorForbiddenText = 'Ошибка доступа (403). Проверьте, что вы авторизованы как администратор.';
  errorSessionExpiredText = 'Сессия истекла. Пожалуйста, войдите снова.';
  errorLoadDefaultText = 'Ошибка при загрузке пользователя';
  errorValidationDefaultText = 'Ошибка валидации данных';
  errorSaveDefaultText = 'Ошибка при сохранении пользователя';

  private subscriptions = new Subscription();

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private languageService: LanguageService,
    private userService: UserService,
    private dictionaryService: DictionaryService,
    private authService: AuthService,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef
  ) {
    this.userForm = this.fb.group({
      fullName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: [''],
      roleId: ['', [Validators.required]],
      password: ['', []], // Валидация будет добавлена динамически
      comment: ['']
    });
  }

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('UserEdit: Running on server (SSR), skipping initialization');
      return;
    }

    this.updateTranslations();
    this.subscriptions.add(
      this.languageService.getTranslations().subscribe(() => {
        this.updateTranslations();
        this.cdr.markForCheck();
      })
    );

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        console.warn('UserEdit: User not authenticated, redirecting to login');
        this.router.navigate(['/login']);
        return;
      }

      const user = this.authService.getUser();
      if (user?.role?.code !== 'ROLE_ADMIN') {
        console.warn('UserEdit: User is not admin, redirecting to home');
        this.router.navigate(['/']);
        return;
      }

      const id = this.route.snapshot.paramMap.get('id');
      if (id) {
        this.isEditMode = true;
        this.userId = +id;
        this.loadUser(+id);
      } else {
        // В режиме создания пароль обязателен
        const passwordControl = this.userForm.get('password');
        if (passwordControl) {
          passwordControl.setValidators([Validators.required]);
          passwordControl.updateValueAndValidity();
        }
      }

      this.loadRoles();
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return !value || value === key ? fallback : value;
  }

  private updateTranslations(): void {
    this.titleText = this.t('admin.userEdit.title', 'Создать пользователя');
    this.editTitleText = this.t('admin.userEdit.editTitle', 'Редактировать пользователя');
    this.backText = this.t('admin.userEdit.back', 'Назад');
    this.submitText = this.t('admin.userEdit.submit', 'Создать');
    this.editSubmitText = this.t('admin.userEdit.editSubmit', 'Сохранить');
    this.submittingText = this.t('admin.userEdit.submitting', 'Создание...');
    this.editSubmittingText = this.t('admin.userEdit.editSubmitting', 'Сохранение...');
    this.nameLabel = this.t('admin.users.name', 'Имя');
    this.emailLabel = this.t('admin.users.email', 'Email');
    this.phoneLabel = this.t('admin.users.phone', 'Телефон');
    this.roleLabel = this.t('admin.users.role', 'Роль');
    this.passwordLabel = this.t('admin.userEdit.password', 'Пароль');
    this.passwordPlaceholder = this.t('admin.userEdit.passwordPlaceholder', 'Оставьте пустым, чтобы не менять');
    this.commentLabel = this.t('admin.userEdit.comment', 'Комментарий');
    this.cancelText = this.t('common.cancel', 'Отмена');
    this.selectRoleText = this.t('admin.userEdit.selectRole', 'Выберите роль');
    this.loadingText = this.t('admin.users.loading', 'Загрузка...');
    this.nameRequiredError = this.t('admin.userEdit.nameRequired', 'Имя обязательно');
    this.emailRequiredError = this.t('admin.userEdit.emailRequired', 'Email обязателен');
    this.emailInvalidError = this.t('admin.userEdit.emailInvalid', 'Email должен быть валидным');
    this.roleRequiredError = this.t('admin.userEdit.roleRequired', 'Роль обязательна');
    this.fieldRequiredError = this.t('admin.userEdit.fieldRequired', 'Поле обязательно');
    this.errorNotFoundText = this.t('admin.userView.errorNotFound', 'Пользователь не найден');
    this.errorForbiddenText = this.t('admin.userView.errorForbidden', 'Ошибка доступа (403). Проверьте, что вы авторизованы как администратор.');
    this.errorSessionExpiredText = this.t('admin.userView.errorSessionExpired', 'Сессия истекла. Пожалуйста, войдите снова.');
    this.errorLoadDefaultText = this.t('admin.userView.errorLoadDefault', 'Ошибка при загрузке пользователя');
    this.errorValidationDefaultText = this.t('admin.userEdit.errorValidationDefault', 'Ошибка валидации данных');
    this.errorSaveDefaultText = this.t('admin.userEdit.errorSaveDefault', 'Ошибка при сохранении пользователя');
  }

  private loadUser(id: number): void {
    this.loading = true;
    this.error = null;

    this.subscriptions.add(
      this.userService.getUser(id).subscribe({
        next: (user) => {
          this.user = user;
          this.populateForm(user);
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('UserEdit: Error loading user', err);
          if (err.status === 404) {
            this.error = this.errorNotFoundText;
          } else if (err.status === 403) {
            this.error = this.errorForbiddenText;
          } else if (err.status === 401) {
            this.error = this.errorSessionExpiredText;
          } else {
            this.error = this.errorLoadDefaultText;
          }
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  private populateForm(user: UserDto): void {
    this.userForm.patchValue({
      fullName: user.fullName,
      email: user.email,
      phone: user.phone || '',
      roleId: user.role?.id || '',
      password: '', // Не заполняем пароль при редактировании
      comment: user.comment || ''
    });

    // В режиме редактирования пароль не обязателен
    if (this.isEditMode) {
      const passwordControl = this.userForm.get('password');
      if (passwordControl) {
        passwordControl.clearValidators();
        passwordControl.updateValueAndValidity();
      }
    }
  }

  private loadRoles(): void {
    this.subscriptions.add(
      this.dictionaryService.getRoles().subscribe({
        next: (roles) => {
          this.roles = roles;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('UserEdit: Error loading roles', err);
        }
      })
    );
  }

  onSubmit(): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    this.submitting = true;
    this.error = null;

    const formValue = this.userForm.value;
    const request: CreateUpdateUserRequest = {
      fullName: formValue.fullName,
      email: formValue.email,
      phone: formValue.phone || undefined,
      roleId: formValue.roleId,
      password: formValue.password || undefined,
      comment: formValue.comment || undefined
    };

    // В режиме редактирования не отправляем пароль, если он пустой
    if (this.isEditMode && !request.password) {
      delete request.password;
    }

    const operation = this.isEditMode && this.userId
      ? this.userService.updateUser(this.userId, request)
      : this.userService.createUser(request);

    this.subscriptions.add(
      operation.subscribe({
        next: (user) => {
          this.submitting = false;
          this.router.navigate(['/admin/users', user.id]);
        },
        error: (err) => {
          console.error('UserEdit: Error saving user', err);
          if (err.status === 403) {
            this.error = this.errorForbiddenText;
          } else if (err.status === 401) {
            this.error = this.errorSessionExpiredText;
          } else if (err.status === 400) {
            this.error = err.error?.message || this.errorValidationDefaultText;
          } else {
            this.error = this.errorSaveDefaultText;
          }
          this.submitting = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  getFieldError(fieldName: string): string {
    const field = this.userForm.get(fieldName);
    if (field?.hasError('required')) {
      if (fieldName === 'fullName') return this.nameRequiredError;
      if (fieldName === 'email') return this.emailRequiredError;
      if (fieldName === 'roleId') return this.roleRequiredError;
      return this.fieldRequiredError;
    }
    if (field?.hasError('email')) {
      return this.emailInvalidError;
    }
    return '';
  }
}
